/**
 * Parse invite link query params from the current URL (or a given URL string).
 * All query params are treated as variable names/values for the session.
 * No reserved params in this phase.
 */

export interface ParsedInviteLink {
  linkVariables?: Record<string, string>;
}

/**
 * Normalize query string: replace HTML entity &amp; with & so links copied from HTML (e.g. email) parse correctly.
 */
function normalizeSearch(search: string): string {
  return search.replace(/&amp;/gi, '&');
}

/**
 * Parse query string into link variables.
 * Keys and values are decoded (decodeURIComponent) and trimmed; empty keys are dropped.
 * Replaces &amp; with & so URLs copied from HTML work (e.g. ?a=1&amp;b=2).
 */
export function parseInviteLinkFromUrl(url: URL | string): ParsedInviteLink {
  if (typeof url === 'string') {
    const ampIndex = url.indexOf('?');
    if (ampIndex !== -1) {
      url = url.slice(0, ampIndex + 1) + normalizeSearch(url.slice(ampIndex + 1));
    }
  }
  const urlObj = typeof url === 'string' ? new URL(url, 'http://localhost') : url;
  const linkVariables: Record<string, string> = {};

  urlObj.searchParams.forEach((value, key) => {
    const trimmedKey = key.trim();
    if (!trimmedKey) return;
    try {
      const decodedValue = decodeURIComponent(value).trim();
      linkVariables[trimmedKey] = decodedValue;
    } catch {
      linkVariables[trimmedKey] = value.trim();
    }
  });

  if (Object.keys(linkVariables).length === 0) {
    return {};
  }
  return { linkVariables };
}

/**
 * Parse query string (e.g. location.search) into link variables.
 * Use this when you have the search string at a specific moment (e.g. from React Router location).
 */
export function parseInviteLinkFromSearch(search: string): ParsedInviteLink {
  if (!search || typeof search !== 'string') return {};
  const withQuestion = search.startsWith('?') ? search : `?${search}`;
  const trimmed = normalizeSearch(withQuestion.slice(1));
  if (!trimmed.trim()) return {};
  const linkVariables: Record<string, string> = {};
  const params = new URLSearchParams(trimmed);
  params.forEach((value, key) => {
    const trimmedKey = key.trim();
    if (!trimmedKey) return;
    try {
      const decodedValue = decodeURIComponent(value).trim();
      linkVariables[trimmedKey] = decodedValue;
    } catch {
      linkVariables[trimmedKey] = value.trim();
    }
  });
  if (Object.keys(linkVariables).length === 0) return {};
  return { linkVariables };
}

/**
 * Parse invite link from window.location (for use in browser).
 */
export function parseInviteLinkFromWindow(): ParsedInviteLink {
  if (typeof window === 'undefined') return {};
  return parseInviteLinkFromUrl(window.location.href);
}
